// Assignment time limits (see server/assignmentTiming.js). An assignment is
// untimed only when it says so explicitly; one without these fields is a
// legacy assignment timed by its quiz.
export const MIN_ASSIGNMENT_MINUTES = 1;
export const MAX_ASSIGNMENT_MINUTES = 180;

export const isUntimedAssignment = (assignment) =>
  assignment?.timeLimitMode === "untimed" && assignment?.timeLimitSeconds === null;

// A quiz's defaultTimeLimit (seconds) means "no timer" when it is missing or
// zero, which is how the server reads it when an attempt starts.
export const quizHasNoTimeLimit = (defaultTimeLimit) =>
  defaultTimeLimit === undefined || defaultTimeLimit === null || defaultTimeLimit === 0;

// The modal's starting choice: an untimed quiz starts as No timer rather than
// being given a duration, a timed one starts with its own duration in minutes.
export const defaultAssignmentTiming = (quiz) => {
  const seconds = quiz?.defaultTimeLimit;
  if (quizHasNoTimeLimit(seconds)) return { mode: "untimed", minutes: "" };
  if (Number.isFinite(seconds) && seconds > 0) {
    return { mode: "timed", minutes: String(Math.max(MIN_ASSIGNMENT_MINUTES, Math.round(seconds / 60))) };
  }
  return { mode: "timed", minutes: "" };
};

// The request fields for a choice, or an error message for an invalid duration.
export const assignmentTimingRequest = ({ mode, minutes }) => {
  if (mode === "untimed") return { settings: { timeLimitMode: "untimed", timeLimitSeconds: null }, error: "" };
  const trimmed = String(minutes).trim();
  const value = Number(trimmed);
  if (!/^\d+$/.test(trimmed) || value < MIN_ASSIGNMENT_MINUTES || value > MAX_ASSIGNMENT_MINUTES) {
    return {
      settings: null,
      error: `Enter a time limit from ${MIN_ASSIGNMENT_MINUTES} to ${MAX_ASSIGNMENT_MINUTES} whole minutes, or choose No timer.`,
    };
  }
  return { settings: { timeLimitMode: "timed", timeLimitSeconds: value * 60 }, error: "" };
};

// Whether the server stored exactly the requested settings, work type
// included. An older server accepts the request but ignores the fields, so it
// echoes nothing (or, before Homework existed, could not store Homework).
export const serverAcceptedTiming = (requested, echoed) =>
  Boolean(echoed) &&
  echoed.workType === (requested.workType || "assessment") &&
  echoed.timeLimitMode === requested.timeLimitMode &&
  echoed.timeLimitSeconds === requested.timeLimitSeconds;

export const describeAssignmentTiming = (settings) =>
  settings.timeLimitMode === "untimed"
    ? "No timer"
    : `${settings.timeLimitSeconds / 60} ${settings.timeLimitSeconds === 60 ? "minute" : "minutes"}`;

export const describeAssignmentSettings = (settings) =>
  `${settings.workType === "homework" ? "Homework" : "Assessment"} · ${describeAssignmentTiming(settings)}`;
